import { type ReactElement, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useBankTransferOptionQuery, usePaymentCheckoutQuery } from '@/entities/payment';
import { BankTransferPanel, PaymentMethodChoice } from '@/features/bank-transfer';
import { PaymentWidgetPanel, paymentApiErrorMessage } from '@/features/payment-checkout';
import { orderDepositPath, PAYMENT_CHECKOUT_ORDER_PARAM } from '@/shared/config';
import { Button, EyebrowLabel, SitePageShell } from '@/shared/ui';
import { SiteFooter } from '@/widgets/site-footer';
import { SiteHeader } from '@/widgets/site-header';

/** 머리 바로 아래 가운데 안내 — 머리의 얇은 아랫선과 겹치지 않게 아랫선만 둔다. */
const BLOCK_CLASS =
  'flex flex-col items-center gap-4 border-b border-foreground/10 py-16 text-center';

/** ?orderId= 값 — 비었거나 공백뿐이면 지정되지 않은 것으로 본다. */
const parseOrderIdParam = (raw: string | null): string | null =>
  raw === null || raw.trim() === '' ? null : raw;

/**
 * 결제 페이지(?orderId=) — 결제 정보를 조회해 주문서형 결제 UI 를 띄운다(결제 설계 §4-1·§11).
 * 머리는 내 견적 상세와 같은 결(작은 영문 머리말·큰 굵은 제목·얇은 아랫선)이고, 본문은 셸 전폭을 쓴다.
 */
export const PaymentCheckoutPage = (): ReactElement => {
  const [params] = useSearchParams();
  const orderId = parseOrderIdParam(params.get(PAYMENT_CHECKOUT_ORDER_PARAM));
  const checkoutQuery = usePaymentCheckoutQuery(orderId ?? '', { enabled: orderId !== null });

  const [method, setMethod] = useState<'CARD' | 'BANK_TRANSFER'>('CARD');
  const bankOption = useBankTransferOptionQuery(orderId ?? '', {
    enabled: orderId !== null && checkoutQuery.data?.payable === true,
  });

  let body: ReactElement;

  if (orderId === null) {
    body = (
      <div className={BLOCK_CLASS}>
        <p role="alert" className="text-sm text-destructive">
          결제할 주문이 지정되지 않았습니다.
        </p>
      </div>
    );
  } else if (checkoutQuery.isPending) {
    // 결제 화면과 같은 두 칸 자리표시 — 문구는 화면낭독기에만 읽힌다.
    body = (
      <div>
        <p role="status" className="sr-only">
          결제 정보를 불러오는 중입니다.
        </p>
        <div
          aria-hidden
          className="grid gap-10 lg:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)] lg:gap-12 xl:gap-16"
        >
          <div className="min-h-80 animate-pulse bg-muted/70" />
          <div className="min-h-56 animate-pulse bg-muted/70" />
        </div>
      </div>
    );
  } else if (checkoutQuery.isError) {
    body = (
      <div role="alert" className={BLOCK_CLASS}>
        {/* 서버가 답한 안내만 그대로 — 통신 오류 원문(Failed to fetch 등)은 우리 문구로 바꾼다. */}
        <p className="text-sm text-destructive">
          {paymentApiErrorMessage(checkoutQuery.error, '결제 정보를 불러오지 못했습니다.')}
        </p>
        <Button
          variant="outline"
          className="h-11 border-foreground/20 px-6"
          onClick={() => {
            void checkoutQuery.refetch();
          }}
        >
          다시 시도
        </Button>
      </div>
    );
  } else if (checkoutQuery.data.payable && checkoutQuery.data.openBankTransfer) {
    body = (
      <div className={BLOCK_CLASS}>
        <p>계좌이체로 주문한 건입니다.</p>
        <p className="text-sm text-muted-foreground">
          카드로 바꾸려면 입금 안내 화면에서 결제 방법 바꾸기를 선택해 주세요.
        </p>
        <Link to={orderDepositPath(orderId)} className="text-sm underline underline-offset-4">
          입금 안내 화면으로 이동
        </Link>
      </div>
    );
  } else if (!checkoutQuery.data.payable) {
    body = (
      <div className={BLOCK_CLASS}>
        <p role="alert" className="text-sm text-destructive">
          {checkoutQuery.data.reason}
        </p>
      </div>
    );
  } else {
    body = (
      <div className="flex flex-col gap-8">
        <PaymentMethodChoice
          value={method}
          onChange={setMethod}
          bankAvailable={bankOption.data?.available === true}
        />
        {method === 'BANK_TRANSFER' && bankOption.data?.available ? (
          <BankTransferPanel
            key={orderId}
            orderId={orderId}
            option={bankOption.data}
            onPlaced={() => {
              void checkoutQuery.refetch();
            }}
          />
        ) : (
          <PaymentWidgetPanel
            checkout={checkoutQuery.data}
            onAmountChanged={() => {
              void checkoutQuery.refetch();
            }}
          />
        )}
      </div>
    );
  }

  return (
    <SitePageShell header={<SiteHeader />} footer={<SiteFooter />}>
      <div className="flex flex-col gap-10 sm:gap-12">
        <header className="flex flex-col gap-5 border-b border-foreground/10 pb-8 lg:flex-row lg:items-end lg:justify-between lg:gap-16">
          <div className="flex flex-col gap-4">
            <EyebrowLabel>Payment</EyebrowLabel>
            <h1 className="text-[clamp(2.25rem,4.5vw,4rem)] leading-none font-bold tracking-[-0.03em]">
              결제
            </h1>
          </div>
          <p className="max-w-md text-sm leading-relaxed text-muted-foreground lg:max-w-xl lg:pb-1 lg:text-right">
            주문 내용과 금액을 확인하고 결제 수단을 골라 결제해 주세요.
          </p>
        </header>
        {body}
      </div>
    </SitePageShell>
  );
};
